// Serah Terima ke Customer — ujung alur pesanan (K166 butir 1 · Q25/K167 · S11).
//
// ⛔ LAHIR DARI SENSUS PINTU (23 Sep 2026): enam rute backend serah terima customer berdiri
//    tanpa SATU layar pun — alur pesanan tidak bisa mencapai "diserahkan" dari aplikasi.
//    Layar ini hanya PINTU: aturan peran, wilayah, status, dan metode seluruhnya ditegakkan
//    `backend/services/serah_terima_customer.py`. Tombol yang tampil di sini mengikuti peran
//    supaya orang tidak menekan yang pasti ditolak — bukan sebagai keamanan.
//
// ⚠ Daftar dihitung dari KEADAAN (bukan peristiwa): kalau tidak ada yang mengerjakannya, ia
//    tetap terisi dan bertambah. `?fa=<id>` (tujuan scan label QR) menyorot satu FA.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { apiErrorMessage } from "../lib/inventoryApi";
import { petaNamaCustomer } from "../lib/petaNamaCustomer";
import { SERAH_TERIMA_UBAH_METODE_ROLES, FA_ISI_ROLES } from "../lib/roles";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { isianKirimLengkap, isianPengambilLengkap, nilaiPengambil } from "../lib/serahTerimaKirim";

const METODE_LABEL = {
  AMBIL_DI_CABANG: "Ambil di cabang",
  TITIP_AMBIL_CABANG: "Titip ambil di cabang lain",
  KIRIM_KE_ALAMAT: "Dikirim ke alamat",
};

const BAGIAN = [
  { kunci: "menunggu_siap", judul: "Lunas atau tanpa biaya — belum disiapkan", aksi: "siap",
    ket: "Barang lunas (atau pesanan tanpa biaya yang disetujui Owner) yang belum ditandai siap diserahkan. Pesanan yang lunas sebelum produksinya selesai tampil \"Masih diproduksi\"." },
  { kunci: "menunggu_berangkat", judul: "Siap — titipan belum berangkat", aksi: "berangkat",
    ket: "Titip-ambil: barang belum dicatat berangkat dari cabang asal." },
  { kunci: "menunggu_diambil", judul: "Titipan sudah berangkat — belum diambil", aksi: "diserahkan",
    ket: "Umur dihitung dari stempel berangkat." },
  { kunci: "menunggu_diserahkan", judul: "Siap — belum diserahkan", aksi: "diserahkan",
    ket: "Semua barang siap yang belum diterima customer, apa pun metodenya." },
];

export default function SerahTerimaCustomer() {
  const { profile } = useAuth();
  const role = profile?.role?.code;
  const [params] = useSearchParams();
  const sorot = params.get("fa");
  const [data, setData] = useState(null);
  const [nama, setNama] = useState({});
  const [toko, setToko] = useState([]);
  const [err, setErr] = useState(null);
  const [sibuk, setSibuk] = useState(null);
  const [metodeUntuk, setMetodeUntuk] = useState(null);   // baris yang sedang diubah metodenya
  const [catatanUntuk, setCatatanUntuk] = useState(null); // baris yang sedang diserahkan
  const bolehUbahMetode = SERAH_TERIMA_UBAH_METODE_ROLES.includes(role);
  // K206 butir 6 (12 Okt 2026): nama customer hanya untuk Owner/KC/CS (pintu `/customers` menolak peran lain).
  // Admin Gudang mengenali bungkusan lewat NAMA TIM yang ikut di antrean.
  const bolehNamaCustomer = FA_ISI_ROLES.includes(role);

  const muat = useCallback(async () => {
    setErr(null);
    try {
      const r = await api.get("/serah-terima/antrean");
      setData(r.data);
      if (bolehNamaCustomer) {
        const ids = Object.values(r.data || {}).flat().map((f) => f.customer_id);
        try { setNama(await petaNamaCustomer(ids)); } catch { /* jatuh ke nomor FA + nama tim */ }
      }
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat antrean serah terima."));
    }
  }, [bolehNamaCustomer]);

  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    api.get("/stores").then((r) => setToko(r.data || []))
      .catch((e) => { setToko([]); toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat — pilihan cabang ambil kosong.")); });
  }, []);
  const namaToko = useMemo(() => Object.fromEntries(toko.map((t) => [t.id, t.name])), [toko]);

  async function jalankan(aksi, fa, body) {
    setSibuk(`${aksi}:${fa.id}`);
    try {
      if (aksi === "siap") await api.post(`/serah-terima/${fa.id}/siap`);
      else if (aksi === "berangkat") await api.post(`/serah-terima/${fa.id}/berangkat`);
      else if (aksi === "diserahkan") await api.post(`/serah-terima/${fa.id}/diserahkan`, body || {});
      else if (aksi === "metode") await api.patch(`/serah-terima/${fa.id}/metode`, body);
      toast.success(`${fa.fa_number}: tersimpan`);
      setMetodeUntuk(null); setCatatanUntuk(null);
      await muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan."));
    } finally {
      setSibuk(null);
    }
  }

  if (err) return <div className="p-4 text-sm text-danger" data-testid="serah-terima-galat">{err}</div>;
  if (!data) return <div className="p-4 text-sm text-muted-foreground">Memuat…</div>;

  return (
    <div className="space-y-4" data-testid="serah-terima-page">
      <h1 className="text-2xl font-bold">Serah Terima ke Customer</h1>
      {BAGIAN.map((b) => {
        const baris = data[b.kunci] || [];
        return (
          <Card key={b.kunci} className="p-0 overflow-hidden" data-testid={`serah-terima-${b.kunci}`}>
            <div className="px-4 py-3 border-b border-border">
              <div className="font-semibold">{b.judul} <span className="text-muted-foreground font-normal">({baris.length})</span></div>
              <div className="text-xs text-muted-foreground">{b.ket}</div>
            </div>
            {baris.length === 0 ? (
              <div className="px-4 py-3 text-sm text-muted-foreground">Tidak ada.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-2">FA</th>
                      {bolehNamaCustomer && <th className="text-left">Customer</th>}
                      <th className="text-left">Tim</th>
                      <th className="text-left">Metode</th>
                      <th className="text-left">Umur</th>
                      <th className="text-right px-4">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baris.map((fa) => (
                      <tr key={fa.id} className={`border-b border-border ${sorot === fa.id ? "bg-warning-soft" : ""}`}
                          data-testid={`serah-terima-baris-${fa.id}`}>
                        <td className="px-4 py-2 font-medium"><Link to={`/fa-orders/${fa.id}`} className="underline">{fa.fa_number}</Link></td>
                        {bolehNamaCustomer && <td>{nama[fa.customer_id] || "—"}</td>}
                        <td data-testid={`serah-terima-tim-${fa.id}`}>{fa.nama_team || "—"}</td>
                        <td>
                          {METODE_LABEL[fa.metode_pengiriman] || <span className="text-warning-soft-foreground">belum ditentukan</span>}
                          {fa.metode_pengiriman === "TITIP_AMBIL_CABANG" && fa.cabang_ambil_id && (
                            <span className="text-xs text-muted-foreground"> → {namaToko[fa.cabang_ambil_id] || "cabang"}</span>)}
                        </td>
                        <td>{fa.umur_hari != null ? `${fa.umur_hari} hari` : "—"}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap space-x-2">
                          {bolehUbahMetode && b.aksi !== "diserahkan" && (
                            <Button size="sm" variant="outline" onClick={() => setMetodeUntuk(fa)}
                                    data-testid={`serah-terima-metode-${fa.id}`}>Ubah metode</Button>
                          )}
                          {b.aksi === "diserahkan" ? (
                            <Button size="sm" disabled={!!sibuk} onClick={() => setCatatanUntuk(fa)}
                                    data-testid={`serah-terima-diserahkan-${fa.id}`}>Diserahkan</Button>
                          ) : b.aksi === "siap" && fa.barang_jadi === false ? (
                            // K210 BUSINESS-77: lunas dini — barangnya belum selesai diproduksi (server juga menolak).
                            <span className="text-xs text-muted-foreground" data-testid={`serah-terima-belum-jadi-${fa.id}`}>
                              Masih diproduksi
                            </span>
                          ) : (
                            <Button size="sm" disabled={!!sibuk} onClick={() => jalankan(b.aksi, fa)}
                                    data-testid={`serah-terima-${b.aksi}-${fa.id}`}>
                              {b.aksi === "siap" ? "Tandai siap" : "Berangkat"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      {metodeUntuk && (
        <DialogMetode fa={metodeUntuk} toko={toko} sibuk={!!sibuk}
                      onBatal={() => setMetodeUntuk(null)}
                      onSimpan={(body) => jalankan("metode", metodeUntuk, body)} />
      )}
      {catatanUntuk && (
        <DialogDiserahkan fa={catatanUntuk} sibuk={!!sibuk}
                          namaCustomer={bolehNamaCustomer ? (nama[catatanUntuk.customer_id] || "") : ""}
                          onBatal={() => setCatatanUntuk(null)}
                          onSimpan={(body) => jalankan("diserahkan", catatanUntuk, body)} />
      )}
    </div>
  );
}

function Lapis({ children }) {
  return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">{children}</div>;
}

function DialogMetode({ fa, toko, sibuk, onBatal, onSimpan }) {
  const [metode, setMetode] = useState(fa.metode_pengiriman || "");
  const [cabang, setCabang] = useState(fa.cabang_ambil_id || "");
  // Pilihan cabang: SATU wilayah dengan pesanan (vonis Owner 11 Sep) — backend tetap penjaganya.
  const cabangSah = toko.filter((t) => t.region_id === fa.region_id);
  return (
    <Lapis>
      <Card className="p-4 w-full max-w-md space-y-3" data-testid="serah-terima-dialog-metode">
        <div className="font-semibold">Metode serah terima — {fa.fa_number}</div>
        <select value={metode} onChange={(e) => setMetode(e.target.value)}
                className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="serah-terima-pilih-metode">
          <option value="">— belum ditentukan —</option>
          {Object.entries(METODE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {metode === "TITIP_AMBIL_CABANG" && (
          <select value={cabang} onChange={(e) => setCabang(e.target.value)}
                  className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="serah-terima-pilih-cabang">
            <option value="">— pilih cabang ambil —</option>
            {cabangSah.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onBatal}>Batal</Button>
          <Button disabled={sibuk} onClick={() => onSimpan({
            metode: metode || null,
            cabang_ambil_id: metode === "TITIP_AMBIL_CABANG" ? (cabang || null) : null,
          })} data-testid="serah-terima-simpan-metode">Simpan</Button>
        </div>
      </Card>
    </Lapis>
  );
}

// T76 (titipan auditor 27 Okt 2026): kiriman ke alamat WAJIB berkurir + bernomor resi (server juga menolak tanpa
// keduanya). Nomor resi tampil di Ruang Pesanan customer dan dikabarkan lewat WA (atau tugas CS menyalin).
// K237 (BUSINESS-116 b): barang yang DIAMBIL wajib bernama pengambil — terisi otomatis nama customer, diganti bila
// orang lain. Nama ini dikabarkan ke customer ("sudah diserahkan kepada …; bila bukan Anda, balas pesan ini").
// Peran tanpa hak nama customer (Admin Gudang, K206) memilih "Customer pemesan sendiri" — server mengisi namanya.
function DialogDiserahkan({ fa, sibuk, onBatal, onSimpan, namaCustomer }) {
  const [catatan, setCatatan] = useState("");
  const [kurir, setKurir] = useState("");
  const [resi, setResi] = useState("");
  const adaNama = !!(namaCustomer || "").trim();
  const [customerSendiri, setCustomerSendiri] = useState(!adaNama);
  const [pengambil, setPengambil] = useState(namaCustomer || "");
  const kiriman = fa.metode_pengiriman === "KIRIM_KE_ALAMAT";
  const kurang = kiriman ? !isianKirimLengkap(kurir, resi) : !isianPengambilLengkap(customerSendiri, pengambil);
  const kelasIsian = "w-full border border-input rounded px-2 py-1.5 text-sm";
  return (
    <Lapis>
      <Card className="p-4 w-full max-w-md space-y-3" data-testid="serah-terima-dialog-diserahkan">
        <div className="font-semibold">
          {kiriman ? "Barang dikirim ke customer" : "Barang diterima customer"} — {fa.fa_number}
        </div>
        {kiriman && (
          <div className="space-y-2" data-testid="serah-terima-isian-kirim">
            <p className="text-xs text-muted-foreground">
              Pesanan ini dikirim ke alamat customer. Isi jasa kirim dan nomor resinya — nomor resi dikabarkan ke
              customer dan tampil di Ruang Pesanan.
            </p>
            <input value={kurir} onChange={(e) => setKurir(e.target.value)} maxLength={60}
                   placeholder="Jasa kirim, mis. JNE, J&T, SiCepat" className={kelasIsian}
                   data-testid="serah-terima-kurir" />
            <input value={resi} onChange={(e) => setResi(e.target.value)} maxLength={60}
                   placeholder="Nomor resi" className={kelasIsian} data-testid="serah-terima-resi" />
          </div>
        )}
        {!kiriman && (
          <div className="space-y-2" data-testid="serah-terima-isian-pengambil">
            <label className="text-sm font-medium" htmlFor="serah-terima-pengambil">Diambil oleh (wajib)</label>
            {adaNama ? (
              <p className="text-xs text-muted-foreground">
                Terisi nama customer. Ganti bila yang mengambil orang lain — nama ini dikabarkan ke customer lewat
                WhatsApp.
              </p>
            ) : (
              <div className="space-y-1 text-sm">
                <label className="flex items-center gap-2">
                  <input type="radio" checked={customerSendiri} onChange={() => setCustomerSendiri(true)}
                         data-testid="serah-terima-pengambil-customer" />
                  Customer pemesan sendiri
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" checked={!customerSendiri} onChange={() => setCustomerSendiri(false)}
                         data-testid="serah-terima-pengambil-lain" />
                  Orang lain
                </label>
              </div>
            )}
            {(adaNama || !customerSendiri) && (
              <input id="serah-terima-pengambil" value={pengambil} maxLength={120}
                     onChange={(e) => setPengambil(e.target.value)}
                     placeholder="Nama orang yang mengambil" className={kelasIsian}
                     data-testid="serah-terima-pengambil" />
            )}
          </div>
        )}
        <textarea value={catatan} onChange={(e) => setCatatan(e.target.value)} maxLength={500}
                  placeholder="Catatan (opsional)"
                  className={kelasIsian} rows={3}
                  data-testid="serah-terima-catatan" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onBatal}>Batal</Button>
          <Button disabled={sibuk || kurang} onClick={() => onSimpan({
            catatan: catatan.trim() || null,
            ...(kiriman ? { kurir: kurir.trim(), nomor_resi: resi.trim() }
              : { diambil_oleh: nilaiPengambil(customerSendiri, pengambil) }),
          })} data-testid="serah-terima-simpan-diserahkan">
            {kiriman ? "Nyatakan dikirim" : "Nyatakan diserahkan"}
          </Button>
        </div>
      </Card>
    </Lapis>
  );
}
